import * as THREE from 'three';
import Action from "./../classes/Action";
import { CustomEventTypes, SaveModificationTypes } from "./../globals";
import VisibilityManager from '../classes/VisibilityManager';
import DefaultSceneManager from '../classes/DefaultSceneManager';
import Scenario from "../classes/scenarioElements/Scenario";
import ProjectSaver from "../classes/ProjectSaver";
import {ActionDescription, ActionParameters} from "../classes/api/APIDecorators";
import {RegisterActionWithAPI} from "../classes/api/Decorators";

interface ReparentingData {
	object: THREE.Object3D,
	parent: THREE.Object3D;
	atIndex: number;
	linkedObjects?: THREE.Object3D[];
	linkedObjectParents?: THREE.Object3D[];
}

export interface ISetObjectParentData {
	objects: THREE.Object3D[];
	orderChangeObjects: THREE.Object3D[];
	parentChangeObjects: THREE.Object3D[];
	newParent: THREE.Object3D;
	atIndex: number;
	objectParentMap: ReparentingData[];
	actionName: string;
}

@ActionDescription("Sets the parent of the objects.")
@ActionParameters({objects: "Object3D[]", newParent: "Object3D", atIndex: "number"})
@RegisterActionWithAPI("SetObjectParentAction")
export default class SetObjectParentAction extends Action
{
	static createSetObjectParentData(objects: THREE.Object3D[], newParent: THREE.Object3D, atIndex?: number): ISetObjectParentData
	{
		if (atIndex === undefined)
		{
			atIndex = newParent.children.length;
		}
	
		const orderChangeObjects: THREE.Object3D[] = [];
		const parentChangeObjects: THREE.Object3D[] = [];
	
		let indexOffset = 0;
		for (let i = 0; i < objects.length; i++)
		{
			const object = objects[i];
			if (object.parent !== newParent)
			{
				parentChangeObjects.push(object);
			}
			else
			{
				orderChangeObjects.push(object);
				if (newParent.children.indexOf(object) < atIndex)
				{
					indexOffset++;
				}
			}
		}
		atIndex -= indexOffset;
	
		let actionName: string;
		if (parentChangeObjects.length)
		{
			actionName = "Set " + (objects.length === 1 ? objects[0].name : "Objects") + " Parent";
		}
		else
		{
			actionName = "Reordered Objects";
		}
	
		const isDefaultState = Scenario.currentStep()?.isDefault();
	
		const objectParentMap: ReparentingData[] = [];
		for (const object of objects)
		{
			if (object.parent)
			{
				const data : ReparentingData = {
					object: object,
					parent: object.parent,
					atIndex: object.parent.children.indexOf(object)
				};
	
				if(isDefaultState)
				{
					const linkedObjects = DefaultSceneManager.getInstance().getLinkedObjects(object);
					if(linkedObjects.length)
					{
						data.linkedObjects = linkedObjects;
						data.linkedObjectParents = [];
						for(const linkedObject of linkedObjects)
						{
							data.linkedObjectParents.push(linkedObject.parent!);
						}
					}
				}
	
				objectParentMap.push(data);
			}
		}

		return {
			objects: objects,
			orderChangeObjects: orderChangeObjects,
			parentChangeObjects: parentChangeObjects,
			newParent: newParent,
			atIndex: atIndex,
			objectParentMap: objectParentMap,
			actionName: actionName
		}
	}
	constructor(objects: THREE.Object3D[], newParent: THREE.Object3D, atIndex?: number)
	{
		const dataObject = SetObjectParentAction.createSetObjectParentData(objects, newParent, atIndex);
		super([dataObject.objects, dataObject.orderChangeObjects, dataObject.parentChangeObjects, dataObject.newParent, dataObject.atIndex], [dataObject.objectParentMap], dataObject.actionName, [dataObject.objects, dataObject.orderChangeObjects, dataObject.parentChangeObjects, dataObject.atIndex]);
	}

	static doFunction(objects: THREE.Object3D[], orderChangeObjects: THREE.Object3D[], parentChangeObjects: THREE.Object3D[], newParent: THREE.Object3D, atIndex: number)
	{
		Scenario.currentStep()?.timeline?.resetAnimations();

		if (orderChangeObjects.length)
		{
			ProjectSaver.getInstance().markModifiedObject(newParent, SaveModificationTypes.ReorderedChildren);
			DefaultSceneManager.getInstance().syncChildReordering(orderChangeObjects, atIndex);
			for (const object of orderChangeObjects)
			{
				object.removeFromParent();
			}
		}

		for (let i = 0; i < objects.length; i++)
		{
			const object = objects[i];
			if (parentChangeObjects.includes(object))
			{
				ProjectSaver.getInstance().onReparent(object, newParent);
				DefaultSceneManager.getInstance().syncParenting(object, newParent, atIndex+i);
				newParent.attach(object);
			}
			else
			{
				newParent.add(object);
			}

			newParent.children.splice(atIndex+i, 0, newParent.children.splice(newParent.children.length-1, 1)[0]);
		}

		Scenario.currentStep()?.timeline?.update();

		VisibilityManager.setObjectsVisibilityMode([newParent], [newParent.userData.visibility[VisibilityManager.getRenderMode()]]);
		document.dispatchEvent(new CustomEvent(CustomEventTypes.hierarchyChangedEvent));
	}

	do(objects: THREE.Object3D[], orderChangeObjects: THREE.Object3D[], parentChangeObjects: THREE.Object3D[], newParent: THREE.Object3D, atIndex: number)
	{
		SetObjectParentAction.doFunction(objects, orderChangeObjects, parentChangeObjects, newParent, atIndex);
	}

	static undoFunction(objectParentMap: ReparentingData[])	
	{
		Scenario.currentStep()?.timeline?.resetAnimations();

		for (const data of objectParentMap)
		{
			const object = data.object
			const parent = data.parent
			const childIndex = data.atIndex;
			if (object.parent !== parent)
			{
				ProjectSaver.getInstance().onReparent(object, parent);
				DefaultSceneManager.getInstance().syncParenting(object, parent, childIndex);
				parent.attach(object);
				if(data.linkedObjects?.length)
				{
					for (let i = 0; i < data.linkedObjects.length; i++)
					{
						const linkedObject = data.linkedObjects[i];
						if(linkedObject.userData.unlinkedDefaultObjectGuid === object.userData.guid)
						{
							const linkedObjectParent = data.linkedObjectParents![i];
							DefaultSceneManager.getInstance().syncParenting(linkedObject, linkedObjectParent, childIndex);
						}
					}
				}
			}
			else
			{
				DefaultSceneManager.getInstance().syncChildReordering([object], childIndex);
			}
			const currentIndex = object.parent!.children.indexOf(object);
			if (childIndex !== currentIndex)
			{
				parent.children.splice(childIndex, 0, parent.children.splice(currentIndex, 1)[0]);
			}
			VisibilityManager.setObjectsVisibilityMode([parent], [parent.userData.visibility[VisibilityManager.getRenderMode()]]);
		}

		Scenario.currentStep()?.timeline?.update();
		document.dispatchEvent(new CustomEvent(CustomEventTypes.hierarchyChangedEvent));
	}

	undo(objectParentMap: ReparentingData[])
	{
		SetObjectParentAction.undoFunction(objectParentMap);
	}

	static verifyFunction(objects: THREE.Object3D[], orderChangeObjects: THREE.Object3D[], parentChangeObjects: THREE.Object3D[], atIndex: number)	
	{
		if (parentChangeObjects.length) return true;
		if (!orderChangeObjects.length) return false;

		for (let i = 0; i < objects.length; i++)
		{
			const object = objects[i];
			if (orderChangeObjects.includes(object))
			{
				if ( object.parent == null )
				{
					return false;
				}
				const currentIndex = object.parent.children.indexOf(object);
				const targetIndex = atIndex + i;
				if (currentIndex !== targetIndex)
				{
					return true;
				}
			}
		}

		return false;

	}

	verify(objects: THREE.Object3D[], orderChangeObjects: THREE.Object3D[], parentChangeObjects: THREE.Object3D[], atIndex: number)
	{
		return SetObjectParentAction.verifyFunction(objects, orderChangeObjects, parentChangeObjects, atIndex);
	}
}
